import {z} from '@hono/zod-openapi';

const tokenSchema = z.number().int().safe().nonnegative().nullable();

export const VoiceUsageNormalizedTokenCountsSchema = z
  .object({
    inputText: tokenSchema,
    inputAudio: tokenSchema,
    inputImage: tokenSchema,
    cachedText: tokenSchema,
    cachedAudio: tokenSchema,
    cachedImage: tokenSchema,
    cachedTotal: tokenSchema,
    outputText: tokenSchema,
    outputAudio: tokenSchema,
    inputTotal: tokenSchema,
    outputTotal: tokenSchema,
    total: tokenSchema,
  })
  .strict()
  .superRefine((value, context) => {
    const pairs = [
      ['cachedText', value.cachedText, value.inputText],
      ['cachedAudio', value.cachedAudio, value.inputAudio],
      ['cachedImage', value.cachedImage, value.inputImage],
    ] as const;
    pairs.forEach(([path, cached, input]) => {
      if (cached !== null && input !== null && cached > input) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: 'Cached tokens exceed input tokens',
        });
      }
    });
    const exact = (parts: readonly (number | null)[], total: number | null, path: string) => {
      const knownSum = parts.reduce<number>((sum, part) => sum + (part ?? 0), 0);
      const hasKnownPart = parts.some((part) => part !== null);
      if (total !== null && hasKnownPart && knownSum > total) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: 'Known category tokens exceed total',
        });
      }
      if (total !== null && parts.every((part) => part !== null) && knownSum !== total) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: 'Category total is inconsistent',
        });
      }
    };
    exact([value.inputText, value.inputAudio, value.inputImage], value.inputTotal, 'inputTotal');
    exact(
      [value.cachedText, value.cachedAudio, value.cachedImage],
      value.cachedTotal,
      'cachedTotal'
    );
    exact([value.outputText, value.outputAudio], value.outputTotal, 'outputTotal');
    if (
      value.cachedTotal !== null &&
      value.inputTotal !== null &&
      value.cachedTotal > value.inputTotal
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cachedTotal'],
        message: 'Cached total exceeds input total',
      });
    }
    if (
      value.inputTotal !== null &&
      value.outputTotal !== null &&
      value.total !== null &&
      value.inputTotal + value.outputTotal !== value.total
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total'],
        message: 'Total is inconsistent',
      });
    }
    if (value.total !== null) {
      const inputModalities = [value.inputText, value.inputAudio, value.inputImage];
      const outputModalities = [value.outputText, value.outputAudio];
      const inputLowerBound = Math.max(
        value.inputTotal ?? 0,
        inputModalities.reduce<number>((sum, part) => sum + (part ?? 0), 0)
      );
      const outputLowerBound = Math.max(
        value.outputTotal ?? 0,
        outputModalities.reduce<number>((sum, part) => sum + (part ?? 0), 0)
      );
      if (inputLowerBound + outputLowerBound > value.total) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['total'],
          message: 'Known usage lower bounds exceed total',
        });
      }
    }
  })
  .openapi('VoiceUsageNormalizedTokenCountsSchema');

export type VoiceUsageNormalizedTokenCounts = z.infer<typeof VoiceUsageNormalizedTokenCountsSchema>;
